import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

export class DropdownItem {
    label: string;
    value: any;

    /**
     * Converts search results in DropdownItems
     *
     * Usage example: bind a search function (executeSearch) to DropdownComponent "search" event
     *
     * Example search funciton:
     *   executeSearch(value: string): void {
     *     let filterValue = !!value ? value.toLowerCase() : null;
     *
     *     // Make sure to keep selected item data
     *     let selectedItem = !!this.item._id ? this.service.getItem(this.item.id).map(apiResponse => apiResponse.payload) : Observable.of(<Item>null);
     *
     *     // Update dropdown list using provided search criteria
     *     let searchedItems = this.service.searchItem(<Item>{ name: filterValue }).map(apiResponse => apiResponse.payload);
     *
     *     // Now use Pump method to convert search results into DropdownItems
     *     DropdownItem.Pump(searchedItems, selectedItem, i => new DropdownItem(i.name + ' (' + i.code + ')).subscribe(items => this.dropdownItems = items);
     *   }
     */
    public static Pump<T>(listItems: Observable<T[]>, selectedItem: Observable<T>, mapper: (item: T) => DropdownItem) {
        return forkJoin(selectedItem, listItems).pipe(map(x => {
            let selEntity = x[0];
            let entities = x[1];

            let item = selEntity != null ? mapper(selEntity) : null;
            let items = entities.map(entity => mapper(entity));

            if (item != null && !items.find(i => i.value === item.value)) {
                items.push(item);
            }

            items.sort((a, b) => a.label < b.label ? -1 : 1);
            return items;
        }));
    }

    constructor(label: string, value: any) {
        this.label = label;
        this.value = value;
    }
}

@Component({
    selector: 'app-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.css'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => DropdownComponent),
        multi: true
    }]
})
export class DropdownComponent implements ControlValueAccessor {

    @Input() disabled = false;

    @Input() set items(value) {
        this._items = value;
        this.filteredItems = value;
        this.applyFilter();
    }

    @Input() set filter(filter) {
        this._filter = filter;
        this.applyFilter();
    }

    @Input() placeholder;

    // Tooltip shown in search input area, e.g.: "start typing filter list" or "List entries limited to 100 elements"
    @Input() tip = '';

    @Input() set nofilter(nofilter) {
        this.hasFilter = nofilter === undefined;
    }

    @Input() set dropup(dropup) {
        this.isDropup = dropup !== undefined;
    }

    // Event emitted whenever user types in search input; use it to update items list, e.g.: search items trought WS
    @Output() search: EventEmitter<string> = new EventEmitter();

    private searchFieldUpdated: Subject<string> = new Subject<string>();

    hasFilter: boolean = true;

    isDropup: boolean = false;

    isOpen: boolean = false;

    private value: any;

    _filter: any;

    private _items: DropdownItem[] = [];

    filteredItems: DropdownItem[];

    constructor() {
        this.searchFieldUpdated.asObservable().pipe(debounceTime(500), distinctUntilChanged()).subscribe(_ => this.onlineSearch());
    }

    // Function to call when the value changes.
    onChange = (value: any) => { };

    // Function to call when the input is touched (when input is focused or an item selected).
    onTouched = () => { };

    getSelectedLabel() {
        let label;
        if (this.value != null) {
            for (let i = 0; !label && i < this._items.length; i++) {
                if (this._items[i].value === this.value) {
                    label = this._items[i].label || this.placeholder;
                }
            }
        }

        return label || this.placeholder;
    }

    selectItem(item: DropdownItem) {
        if (!this.disabled) {
            this.writeValue(item.value);
            this.onChange(item.value);
        }
        this.toggleDropdown(false);
        this.onTouched();
        return false;
    }

    // Allows Angular to update the model.
    // Update the model and changes needed for the view here.
    writeValue(value: any) {
        this.value = value;
        this._filter = undefined;
        this.applyFilter();
        return false;
    }

    // Allows Angular to register a function to call when the model changes.
    // Save the function as a property to call later here.
    registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    // Allows Angular to register a function to call when the input has been touched.
    // Save the function as a property to call later here.
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    // Allows Angular to disable the input.
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    toggleDropdown(isOpen: boolean) {
        this.isOpen = isOpen;
        if (!this.isOpen) {
            this.onTouched();
        }
    }

    silentClick(e) {
        e.stopPropagation();
    }

    onFilterKeyUp(event: any) {
        let value: string = event.target.value;
        this.searchFieldUpdated.next(value);
    }

    private sortItems(items) {
        items.sort((i1, i2) => {
            if (i1.label.length === i2.label.length) {
                return i1.label.toLowerCase() < i2.label.toLowerCase() ? -1 : 1;
            } else {
                return i1.label.length < i2.label.length ? -1 : 1;
            }
        });
    }

    private applyFilter() {
        let filterValue = !!this._filter ? this._filter.toLowerCase() : null;
        let results = [];

        if (!this._items) {
            results = [];
        } else if (!filterValue) {
            results = this._items.slice(0);
        } else {
            this._items.forEach(item => {
                if (item.label.toLowerCase().indexOf(filterValue) > -1) {
                    results.push(item);
                }
            });

            this.sortItems(results);
        }

        this.filteredItems = Object.assign([], results);
    }

    private onlineSearch() {
        this.applyFilter();
        this.search.emit(this._filter);
    }

}
